import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { SupportTicket } from '../../../types';
import { useReplyTicket } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../ui/Loading';

interface ReplyTicketModalProps {
  ticket: SupportTicket | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while replying to ticket';
};

const ReplyTicketModal: React.FC<ReplyTicketModalProps> = ({
  ticket,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [message, setMessage] = useState('');
  const replyTicketMutation = useReplyTicket();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket) return;

    if (!message.trim()) {
      toast.error('Message is required');
      return;
    }

    try {
      await replyTicketMutation.mutateAsync({
        ticketNumber: ticket.ticketNumber,
        data: {
          message: message.trim(),
        },
      });
      toast.success('Reply sent successfully');
      onSuccess?.();
      onClose();
      setMessage('');
    } catch (error: any) {
      console.error('Reply ticket error:', error);
      const errorMessage = parseErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setMessage('');
    onClose();
  };

  if (!isOpen || !ticket) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Reply to Ticket</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Ticket Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm space-y-1">
                <div><strong>Ticket:</strong> {ticket.ticketNumber}</div>
                <div><strong>From:</strong> {ticket.email}</div>
                <div><strong>Category:</strong> {ticket.categoryDisplayName}</div>
                <div><strong>Status:</strong> {ticket.status}</div>
              </div>
            </div>

            {/* Original Message */}
            <div>
              <label className="block text-sm font-medium mb-2">Original Message</label>
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                {ticket.message}
              </div>
            </div>

            {/* Reply Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-2">
                Your Reply <span className="text-red-600">*</span>
              </label>
              <textarea
                id="message"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={6}
                placeholder="Enter your reply message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {message.length}/2000 characters
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={replyTicketMutation.isLoading || !message.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {replyTicketMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Sending...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Send className="h-4 w-4 mr-2" />
                    Send Reply
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReplyTicketModal;
